import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

// Convert ISO to "YYYY-MM-DDTHH:MM" for datetime-local input
const toDateTimeLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const adjusted = new Date(d.getTime() - offset * 60000);
    return adjusted.toISOString().slice(0, 16);
  } catch { return ""; }
};
const fromDateTimeLocal = (s) => {
  if (!s) return null;
  try { return new Date(s).toISOString(); }
  catch { return null; }
};

// Shared "Add manual shift" modal used by Staff page, Time Clock page,
// and Payroll. Centralising it here means the manual-entry form looks
// identical no matter which screen the manager started from.
export default function AddShiftModal({ staffId, staffName, defaultLocation, onClose, onSaved }) {
  // Default to today 9am–5pm so the manager just has to confirm in the common case
  const today = new Date(); today.setHours(9, 0, 0, 0);
  const todayEnd = new Date(today); todayEnd.setHours(17, 0, 0, 0);

  const [form, setForm] = useState({
    clock_in: toDateTimeLocal(today.toISOString()),
    clock_out: toDateTimeLocal(todayEnd.toISOString()),
    notes: "",
    breaks: [],
  });
  const [saving, setSaving] = useState(false);

  const addBreak = () => {
    // Default a fresh break to middle of shift, 30 min long
    let mid = "";
    let midEnd = "";
    try {
      const s = new Date(form.clock_in);
      const e = new Date(form.clock_out);
      const midTime = new Date((s.getTime() + e.getTime()) / 2);
      mid = toDateTimeLocal(midTime.toISOString());
      midEnd = toDateTimeLocal(new Date(midTime.getTime() + 30 * 60000).toISOString());
    } catch { /* ignore */ }
    setForm(f => ({ ...f, breaks: [...f.breaks, { start: mid, end: midEnd, label: "Lunch" }] }));
  };
  const updateBreak = (idx, k, v) => {
    const next = [...form.breaks]; next[idx] = { ...next[idx], [k]: v };
    setForm(f => ({ ...f, breaks: next }));
  };
  const removeBreak = (idx) => setForm(f => ({ ...f, breaks: f.breaks.filter((_, i) => i !== idx) }));

  const save = async () => {
    const ci = fromDateTimeLocal(form.clock_in);
    const co = fromDateTimeLocal(form.clock_out);
    if (!ci || !co) { toast.error("Clock in & out are required"); return; }
    if (new Date(co) <= new Date(ci)) { toast.error("Clock out must be after clock in"); return; }
    const breaks = form.breaks.map(b => ({
      start: fromDateTimeLocal(b.start),
      end: fromDateTimeLocal(b.end),
      label: b.label || "",
    })).filter(b => b.start && b.end);
    setSaving(true);
    try {
      await api.post("/timeclock/entries", {
        user_id: staffId,
        clock_in: ci,
        clock_out: co,
        location_id: defaultLocation || null,
        notes: form.notes,
        breaks,
      });
      toast.success(`Shift added for ${staffName}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add shift");
    }
    setSaving(false);
  };

  // Live total preview
  let preview = null;
  try {
    const s = new Date(form.clock_in), e = new Date(form.clock_out);
    if (e > s) {
      const shiftH = (e - s) / 3600000;
      const breakH = form.breaks.reduce((acc, b) => {
        const bs = new Date(b.start), be = new Date(b.end);
        return be > bs && bs >= s && be <= e ? acc + (be - bs) / 3600000 : acc;
      }, 0);
      preview = { shiftH, breakH, paidH: Math.max(0, shiftH - breakH) };
    }
  } catch { /* ignore */ }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="add-shift-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Manual entry</p>
            <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">Add shift — {staffName}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Clock in</p>
              <Input type="datetime-local" value={form.clock_in} onChange={e => setForm(f => ({ ...f, clock_in: e.target.value }))}
                data-testid="manual-clock-in" className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Clock out</p>
              <Input type="datetime-local" value={form.clock_out} onChange={e => setForm(f => ({ ...f, clock_out: e.target.value }))}
                data-testid="manual-clock-out" className="rounded-xl border-slate-200" />
            </div>
          </div>

          {/* Breaks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Breaks ({form.breaks.length})</p>
              <button onClick={addBreak} data-testid="add-break-btn"
                className="text-xs inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-slate-900 text-white hover:bg-slate-800">
                <Plus className="w-3 h-3" /> Add break
              </button>
            </div>
            <div className="space-y-2">
              {form.breaks.length === 0 && (
                <p className="text-xs text-slate-400 italic">No breaks. Tap &ldquo;Add break&rdquo; to log lunch / coffee / etc.</p>
              )}
              {form.breaks.map((b, idx) => (
                <div key={idx} className="bg-[#FAFAFA] rounded-xl p-3 space-y-2" data-testid={`break-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <Input value={b.label} onChange={e => updateBreak(idx, "label", e.target.value)}
                      placeholder="Lunch, Coffee…" className="col-span-11 h-9 rounded-lg border-slate-200 text-sm" />
                    <button onClick={() => removeBreak(idx)} data-testid={`remove-break-${idx}`}
                      className="col-span-1 rounded-lg hover:bg-rose-50 flex items-center justify-center">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="datetime-local" value={b.start} onChange={e => updateBreak(idx, "start", e.target.value)}
                      className="h-9 rounded-lg border-slate-200 text-sm" />
                    <Input type="datetime-local" value={b.end} onChange={e => updateBreak(idx, "end", e.target.value)}
                      className="h-9 rounded-lg border-slate-200 text-sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Notes (optional)</p>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value.slice(0, 500) }))}
              rows={2} placeholder="Reason for manual entry…"
              data-testid="manual-notes"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 resize-none" />
          </div>

          {/* Live preview */}
          {preview && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="shift-preview">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Paid hours</p>
                <p className="font-['Outfit'] text-2xl font-medium text-emerald-700 tabular-nums">{preview.paidH.toFixed(2)}h</p>
              </div>
              <p className="text-xs text-emerald-600 text-right">
                Shift: {preview.shiftH.toFixed(2)}h<br />
                Breaks: -{preview.breakH.toFixed(2)}h
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 px-3 h-10">Cancel</button>
          <Button onClick={save} disabled={saving} data-testid="save-manual-shift"
            className="bg-slate-900 text-white rounded-full h-10 px-5 hover:bg-slate-800 disabled:opacity-50">
            <Check className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Add shift"}
          </Button>
        </div>
      </div>
    </div>
  );
}
